import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import api from '../api/axios';
import logo from '../assets/logo.js';

export default function AppLayout({ children }) {
  const { user, logout, can } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [open, setOpen]         = useState(false);
  const [expanded, setExpanded] = useState({});
  const [canAnnounce, setCanAnnounce] = useState(false);   // only the people the server allows (Gary, Kiran)

  useEffect(() => {
    if (!user) return;
    api.get('/announcements/access').then(({ data }) => setCanAnnounce(!!data.allowed)).catch(() => setCanAnnounce(false));
  }, [user]);

  const isOwner  = user?.role === 'owner';
  const { supported, subscribed, subscribe, unsubscribe } = usePushNotifications(user);
  const isActive = (path) => path && (location.pathname === path || location.pathname.startsWith(path + '/'));
  const itemActive = (i) => (i.children ? i.children.some(itemActive) : i.isActive ? i.isActive(location.pathname) : isActive(i.path));

  // ── Build nav groups based on permissions ──
  const navGroups = [];

  if (can('canViewJobs')) {
    const items = [
      { label:'View Daily Sheet', icon:'ti-list',   path:'/jobs/view' },
      { label:'New Entry',        icon:'ti-plus',   path:'/jobs/new-entry', show: can('canCreateJobs') },
      { label:'Search',           icon:'ti-search', path:'/jobs/search',   show: can('canSearchJobs') },
      { label:'Upload Excel',     icon:'ti-upload', path:'/jobs/upload', show: can('canUploadExcel') },
    ].filter(i => i.show !== false);
    navGroups.push({ group:'Daily Sheet', icon:'ti-file-description', items });
  }

  if (can('canViewInspections')) {
    const items = [
      { label:'View Inspections', icon:'ti-list', path:'/inspections' },
      { label:'New Inspection',   icon:'ti-plus', path:'/inspection/new', show: can('canCreateInspections') },
    ].filter(i => i.show !== false);
    navGroups.push({ group:'Inspections', icon:'ti-clipboard-check', items });
  }

  // Incidents — all staff can access
  navGroups.push({ group:'Incidents', icon:'ti-alert-triangle', items:[
    { label:'Incident Reports', icon:'ti-list',        path:'/incidents' },
    { label:'Report Incident',  icon:'ti-plus',        path:'/incidents/new' },
  ]});

  // Announcements — email staff and share files / videos. The server decides who may use it.
  if (canAnnounce) {
    navGroups.push({ group:'Announcements', icon:'ti-speakerphone', items:[
      { label:'Compose', icon:'ti-pencil', path:'/announcements/new' },
      { label:'Sent',    icon:'ti-send',   path:'/announcements', isActive: (p) => p === '/announcements' || (p.startsWith('/announcements/') && p !== '/announcements/new') },
    ]});
  }

  // HR section — show based on permissions
  const hrItems = [
    { label:'Admin Staff',      icon:'ti-user',         path:'/admin-staff',     show: can('canViewStaff') || can('canManageStaff') || isOwner },
    // CS Staff — employee records (bank, medical, ID details), so owner / Manage Staff only
    { label:'CS Staff', icon:'ti-users-group', show: can('canManageStaff') || isOwner, children:[
      { label:'View Staff', icon:'ti-list', path:'/staff', isActive: (p) => p === '/staff' || (p.startsWith('/staff/') && p !== '/staff/new') },
      { label:'Add Staff',  icon:'ti-plus', path:'/staff/new' },
      { label:'Registrations', icon:'ti-user-plus', path:'/staff-registrations' },   // staff sign themselves up; review & approve here
    ]},
    { label:'Reports',          icon:'ti-chart-bar',    path:'/reports',         show: can('canViewReports') || isOwner },
    { label:'Activity Log',     icon:'ti-activity',     path:'/activity',        show: can('canViewActivity') || isOwner },
  ].filter(i => i.show !== false);
  if (hrItems.length > 0) navGroups.push({ group:'HR', icon:'ti-users', items: hrItems });

  // Settings section — show based on permissions
  if (can('canManageSettings') || isOwner) {
    navGroups.push({ group:'Settings', icon:'ti-settings', items: [
      { label:'Dealerships',           icon:'ti-building', path:'/dealerships' },
      { label:'CarSaloon Sites',        icon:'ti-map-pin',  path:'/locations' },
      { label:'Inspection Location',  icon:'ti-map',      path:'/site-locations' },
      { label:'Services',              icon:'ti-tool',     path:'/services' },
      { label:'Roles & Permissions',   icon:'ti-shield',   path:'/roles', show: isOwner },
    ].filter(i => i.show !== false)});
  }

  // ── Quick shortcut bar — Daily Sheet section pages only ──
  const inDailySheetSection = can('canViewJobs') && (location.pathname.startsWith('/jobs') || location.pathname.startsWith('/walkins'));
  const shortcuts = [
    { label:'View Daily Sheet', icon:'ti-list', path:'/jobs/view' },
    { label:'Walk-in',          icon:'ti-car',  path:'/walkins' },
  ];

  const toggleGroup = (g, currentlyOpen) => setExpanded(e => ({ ...e, [g]: !currentlyOpen }));
  const isGroupOpen = (group) => expanded[group.group] !== undefined
    ? expanded[group.group]
    : group.items.some(itemActive);
  const isSubOpen = (group, item) => expanded[`${group.group}/${item.label}`] !== undefined
    ? expanded[`${group.group}/${item.label}`]
    : item.children.some(itemActive);
  const go = (path) => { navigate(path); setOpen(false); };

  const ROLE_LABEL = user?.role === 'owner' ? 'Owner' : (user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '');

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', fontFamily:'Inter,sans-serif', display:'flex', flexDirection:'column' }}>

      {/* ── Top bar ── */}
      <header style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)', height:'56px', padding:'0 16px', display:'flex', alignItems:'center', gap:'12px', position:'sticky', top:0, zIndex:100, boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
        {/* Logo + name: a real link, so it also works with middle-click / "open in new tab" */}
        <Link to="/" aria-label="Car Saloon — go to the home page" title="Home"
          style={{ display:'flex', alignItems:'center', gap:'12px', flex:1, minWidth:0, textDecoration:'none', color:'inherit' }}>
          <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:'#000', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <img src={logo} alt="" style={{ width:'28px', height:'28px', objectFit:'contain' }}/>
          </div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'13px', fontWeight:'600', color:'var(--text)', lineHeight:1 }}>Car Saloon</div>
            <div style={{ fontSize:'11px', color:'var(--muted)', marginTop:'1px' }}>{ROLE_LABEL}</div>
          </div>
        </Link>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <div style={{ width:'30px', height:'30px', borderRadius:'50%', background:'var(--blue)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:'600', color:'#fff', flexShrink:0 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          {(isOwner || can('canViewStaff') || can('canManageStaff')) && supported && (
            <button onClick={subscribed ? unsubscribe : subscribe}
              title={subscribed ? 'Notifications on — click to disable' : 'Enable push notifications'}
              style={{ background:subscribed?'var(--blue-light)':'transparent', border:`1px solid ${subscribed?'var(--blue)':'var(--border-md)'}`, borderRadius:'var(--radius)', width:'36px', height:'36px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:subscribed?'var(--blue)':'var(--muted)' }}>
              <i className={`ti ${subscribed?'ti-bell':'ti-bell-off'}`} style={{ fontSize:'18px' }} aria-label={subscribed?'Notifications on':'Notifications off'}/>
            </button>
          )}
          <button onClick={() => setOpen(true)} style={{ background:'transparent', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', width:'36px', height:'36px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--muted)' }}>
            <i className="ti ti-menu-2" style={{ fontSize:'18px' }} aria-label="Menu"/>
          </button>
        </div>
      </header>

      {/* ── Drawer overlay ── */}
      {open && <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.3)', zIndex:200, backdropFilter:'blur(2px)' }}/>}

      {/* ── Drawer ── */}
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:'280px', background:'var(--surface)', zIndex:201, transform:open?'translateX(0)':'translateX(100%)', transition:'transform .22s ease', display:'flex', flexDirection:'column', boxShadow:'-4px 0 20px rgba(0,0,0,0.12)' }}>
        <div style={{ padding:'16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:'10px' }}>
          <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'#000', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <img src={logo} alt="" style={{ width:'32px', height:'32px', objectFit:'contain' }}/>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:'13px', fontWeight:'600', color:'var(--text)' }}>{user?.name}</div>
            <div style={{ fontSize:'11px', color:'var(--muted)' }}>{user?.email}</div>
          </div>
          <button onClick={() => setOpen(false)} style={{ background:'transparent', border:'none', cursor:'pointer', color:'var(--muted)', display:'flex', alignItems:'center', justifyContent:'center', width:'28px', height:'28px' }}>
            <i className="ti ti-x" style={{ fontSize:'18px' }}/>
          </button>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'8px' }}>
          {navGroups.map(group => {
            const isOpen = isGroupOpen(group);
            const hasActive = group.items.some(itemActive);
            return (
              <div key={group.group} style={{ marginBottom:'2px' }}>
                <button onClick={() => toggleGroup(group.group, isOpen)} style={{ width:'100%', display:'flex', alignItems:'center', gap:'10px', padding:'9px 10px', borderRadius:'var(--radius)', border:'none', background:hasActive?'var(--blue-light)':'transparent', cursor:'pointer', color:hasActive?'var(--blue)':'var(--text)' }}>
                  <i className={`ti ${group.icon}`} style={{ fontSize:'16px', flexShrink:0 }} aria-hidden="true"/>
                  <span style={{ flex:1, textAlign:'left', fontSize:'13px', fontWeight:'500' }}>{group.group}</span>
                  <i className={`ti ${isOpen?'ti-chevron-up':'ti-chevron-down'}`} style={{ fontSize:'13px', color:'var(--hint)' }} aria-hidden="true"/>
                </button>
                {isOpen && (
                  <div style={{ paddingLeft:'12px', marginTop:'2px' }}>
                    {group.items.map(item => {
                      const row = (it, indent) => {
                        const active = itemActive(it);
                        return (
                          <div key={it.path} onClick={() => go(it.path)}
                            style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 10px', paddingLeft: indent ? '22px' : '10px', borderRadius:'var(--radius)', cursor:'pointer', background:active?'var(--blue)':'transparent', color:active?'#fff':'var(--muted)', marginBottom:'1px' }}>
                            <i className={`ti ${it.icon}`} style={{ fontSize:'14px', flexShrink:0 }} aria-hidden="true"/>
                            <span style={{ fontSize:'13px' }}>{it.label}</span>
                          </div>
                        );
                      };
                      if (!item.children) return row(item, false);

                      // An expandable entry inside the group (e.g. CS Staff under HR)
                      const subOpen = isSubOpen(group, item);
                      const subActive = itemActive(item);
                      return (
                        <div key={item.label}>
                          <div onClick={() => toggleGroup(`${group.group}/${item.label}`, subOpen)}
                            style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 10px', borderRadius:'var(--radius)', cursor:'pointer', color: subActive ? 'var(--blue)' : 'var(--muted)', fontWeight: subActive ? '600' : '400', marginBottom:'1px' }}>
                            <i className={`ti ${item.icon}`} style={{ fontSize:'14px', flexShrink:0 }} aria-hidden="true"/>
                            <span style={{ fontSize:'13px', flex:1 }}>{item.label}</span>
                            <i className={`ti ${subOpen ? 'ti-chevron-up' : 'ti-chevron-down'}`} style={{ fontSize:'12px', color:'var(--hint)' }} aria-hidden="true"/>
                          </div>
                          {subOpen && item.children.map(child => row(child, true))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding:'12px', borderTop:'1px solid var(--border)' }}>
          <button onClick={logout} style={{ width:'100%', padding:'10px', background:'transparent', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', color:'var(--red)', fontSize:'13px', fontWeight:'500', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
            <i className="ti ti-logout" aria-hidden="true"/> Sign Out
          </button>
        </div>
      </div>

      {/* ── Main content ── */}
      <main style={{ flex:1, overflowY:'auto', paddingBottom: inDailySheetSection ? '64px' : 'env(safe-area-inset-bottom, 0px)' }}>{children}</main>

      {/* ── Quick shortcut bar ── */}
      {inDailySheetSection && (
        <nav style={{ position:'fixed', bottom:0, left:0, right:0, background:'var(--surface)', borderTop:'1px solid var(--border)', display:'flex', zIndex:99, paddingBottom:'env(safe-area-inset-bottom)' }}>
          {shortcuts.map(item => {
            const active = isActive(item.path);
            return (
              <div key={item.path} onClick={() => navigate(item.path)}
                style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'8px 4px 6px', cursor:'pointer', color:active?'var(--blue)':'var(--hint)', borderTop:active?'2px solid var(--blue)':'2px solid transparent', background:active?'var(--blue-light)':'transparent', transition:'all .15s' }}>
                <i className={`ti ${item.icon}`} style={{ fontSize:'20px', lineHeight:1 }} aria-hidden="true"/>
                <span style={{ fontSize:'10px', marginTop:'3px', fontWeight:active?'600':'400' }}>{item.label}</span>
              </div>
            );
          })}
        </nav>
      )}
    </div>
  );
}
